import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctContractDemandRequestItem } from '@/generated/gql/graphql';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DeleteActionCell,
  DeleteActionCellProps,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  joinFormatterFactory,
  UseDataEditGridConfig,
} from '~/components/DataGridCustom';
import { CommodityLinkWithDescriptionCell } from '~/components/DataGridCustom/cells/CommodityLinkWithDescriptionCell/CommodityLinkWithDescriptionCell';
import { CuriosityLinkChipCell } from '~/components/DataGridCustom/cells/CuriosityLinkChip/CuriosityLinkChipCell.tsx';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { QuantityCell } from '~/components/DataGridCustom/cells/QuantityCell/QuantityCell';
import { RouteId } from '~/routes';

import { ContractDemandRequestLineItemGridField } from './ContractDemandRequestWidget.types';

export const useContractDemandRequestGridConfig: UseDataEditGridConfig<TsrctContractDemandRequestItem> = ({
  onRowDelete,
  disabled,
}) => {
  // use ref for injecting props into delete action rather than adding the props to the useMemo dependencies
  // because updating the grid config leads to the grid re-doing the columns based on the config
  // but ignoring its initialState (which is used for configuring column width with the remote-state hook)
  const deleteActionPropsRef = useRef<
    Pick<DeleteActionCellProps<TsrctContractDemandRequestItem>, 'disabled' | 'onClick'>
  >({
    onClick: onRowDelete,
    disabled,
  });
  deleteActionPropsRef.current = { onClick: onRowDelete, disabled };

  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'ContractDemandRequestGrid.column' });
  return useMemo<DataGridConfig<TsrctContractDemandRequestItem>>(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });
    return {
      columnVisibilityModel: {
        [ContractDemandRequestLineItemGridField.INCO_TERMS]: false,
        [ContractDemandRequestLineItemGridField.DESCRIPTION]: false,
        [ContractDemandRequestLineItemGridField.COMMODITY]: false,
        [ContractDemandRequestLineItemGridField.TITLE]: false,
        [ContractDemandRequestLineItemGridField.MINIMUM_ORDER_QUANTITY]: false,
        [ContractDemandRequestLineItemGridField.UNIT]: false,
        [ContractDemandRequestLineItemGridField.PRICE_UNIT]: false,
        [ContractDemandRequestLineItemGridField.BASELINE_PRICE_PER_UNIT]: false,
        [ContractDemandRequestLineItemGridField.QUALIFIED_SUPPLIERS]: false,
      },
      columns: [
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        createColumn({
          field: ContractDemandRequestLineItemGridField.MATERIAL,
          minWidth: 250,
          display: 'text',
          valueGetter: (_value, row) => {
            return row.commodityId;
          },
          renderCell: cellFactory(CommodityLinkWithDescriptionCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            needs: {
              permission: Permission.BaseCommodityList,
            },
            description: row.title,
          })),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.COMMODITY,
          minWidth: 250,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.COMMODITY,
          }),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.ORGANIZATION,
          minWidth: 350,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.QUALIFIED_SUPPLIERS,
          renderCell: cellFactory(CuriosityLinkChipCell, { ns: CuriosityNamespaceType.SUPPLIER }),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 350,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.QUANTITY,
          minWidth: 180,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.quantity, unitId: row.unitId })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.UNIT,
          minWidth: 250,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.BASELINE_PRICE_PER_PRICE_UNIT,
          minWidth: 220,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.PRICE_UNIT,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.priceUnit, unitId: row.unitId })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.TOTAL_VALUE,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.BASELINE_PRICE_PER_UNIT,
          minWidth: 180,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CurrencyCell),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.MINIMUM_ORDER_QUANTITY,
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.minimumOrderQuantity, unitId: row.unitId })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.COMMODITY_GROUP,
          minWidth: 250,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.COMMODITY_GROUP,
          }),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.TITLE,
          minWidth: 200,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.DESCRIPTION,
          minWidth: 250,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: ContractDemandRequestLineItemGridField.DELETE_ACTION,
          minWidth: 72,
          width: 72,
          renderHeader: () => null,
          align: 'center',
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params) => <DeleteActionCell {...params} {...deleteActionPropsRef.current} />,
        }),
      ],
    };
  }, [t, tColumn]);
};
